import React, { useEffect, useRef } from 'react';
import { readableOn } from '../contrast';

// Keyboard and on-screen targets are both live on every device. One code path
// covers phone and desktop, and the on-screen targets double as the legend for
// which key does what.
export default function ResponseTargets({
  targets,
  onRespond,
  disabled = false,
  selectedTargetId = null,
}) {
  const handlerRef = useRef(null);
  handlerRef.current = (targetId) => {
    if (disabled) return;
    onRespond(targetId);
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.repeat) return;
      // A keystroke aimed at a control belongs to that control, not to the
      // trial. Space is the case that bites: go/no-go and n-back both bind
      // ' ', so a participant who has tabbed to the Exit button and pressed
      // Space would get the button's activation swallowed by preventDefault()
      // AND a Press logged against the live trial — a broken control plus
      // contaminated measurement data. Only keystrokes dispatched at the page
      // itself (body/document/window) count as responses. One identity check:
      // this handler is on the timing-critical path.
      const src = e.target;
      if (src && src.nodeType === 1 && src !== document.body && src !== document.documentElement) return;
      const match = targets.find((t) => t.keys.includes(e.key));
      if (!match) return;
      e.preventDefault();
      handlerRef.current(match.id);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [targets]);

  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-3)',
        justifyContent: 'center',
        flexWrap: 'wrap',
        width: '100%',
        maxWidth: 560,
      }}
    >
      {targets.map((t) => {
        const isSelected = t.id === selectedTargetId;
        // A swatch colour is stimulus data and does not change with the theme,
        // so its label cannot come from a theme token either — `--text-inverted`
        // is white in light mode, which is ~1.4:1 on a yellow swatch. Derive the
        // label from the swatch's own luminance. Unswatched targets (and the
        // selected state, which reverts to --bg-elevated) still follow the theme.
        const swatchLabelColor = isSelected ? null : readableOn(t.color);

        return (
          <button
            key={t.id}
            type="button"
            disabled={disabled}
            aria-pressed={isSelected}
          // A truthy node has no text child, so the label must reach the
          // accessibility tree via aria-label instead.
            aria-label={t.node ? t.label : undefined}
          // aria-keyshortcuts surfaces the keyboard binding to AT without
          // polluting the accessible name (hint span is aria-hidden).
            aria-keyshortcuts={
              t.keys && t.keys.length > 0
                ? t.keys.map((k) => (k === ' ' ? 'Space' : k)).join(' ')
                : undefined
            }
            onClick={() => handlerRef.current(t.id)}
            style={{
              flex: targets.length === 1 ? '1 1 100%' : '1 1 0',
              minWidth: 96,
              minHeight: t.node ? 96 : 72,
              borderRadius: 'var(--radius-lg)',
              border: isSelected ? '1px solid var(--border-strong)' : '1px solid var(--border-subtle)',
              background: isSelected ? 'var(--bg-elevated)' : (t.color || 'var(--bg-elevated)'),
              color: swatchLabelColor || 'var(--text-primary)',
              fontSize: 'var(--text-panel-size)',
              fontWeight: 700,
              fontFamily: 'inherit',
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled && !isSelected ? 0.45 : 1,
              boxShadow: isSelected ? 'inset 0 0 0 2px var(--border-strong)' : 'none',
              transition: 'opacity var(--transition-fast), transform 0.06s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-1)',
            }}
            onPointerDown={(e) => {
              e.currentTarget.style.transform = 'scale(0.97)';
            }}
            onPointerUp={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {t.node ? t.node : t.label}
            <span aria-hidden="true" style={{ fontSize: 'var(--text-label-size)', fontWeight: 600, opacity: 0.7 }}>{t.hint}</span>
          </button>
        );
      })}
    </div>
  );
}
